import { useCallback, useMemo, useState } from 'react'
import { Button, Link, Dialog, Table } from '@/ui'
import moment from 'moment'
import { sortBy } from 'lodash'

import {
  editAllocationGuide,
  fetchAllocationGuidesSummary,
} from '../../actions/allocationGuideActions'
import { AllocationGuide } from '../../reducers/finances/allocationGuidesReducer'
import { DATE_FORMATS } from '../../utils/dateHelpers'
import { useAnalyticsTrack } from '@/features/amplitude'
import { useAppDispatch } from '../../utils/typeHelpers'

export const AllocationGuidesTable = ({
  allocationGuides,
}: {
  allocationGuides: { [key: number]: AllocationGuide }
}) => {
  const dispatch = useAppDispatch()
  const [selectedId, setSelectedId] = useState<number>()

  const track = useAnalyticsTrack()

  const handleUpdateAllocationGuide = useCallback(async () => {
    if (!selectedId) {
      return
    }
    track('submitted allocation guide update')
    await editAllocationGuide(selectedId)(dispatch)
    await fetchAllocationGuidesSummary()(dispatch)
    setSelectedId(undefined)
  }, [dispatch, selectedId, track])

  const handleUpdate = useCallback(
    (id: number, date: string) => {
      const dateMoment = moment(date)
      setSelectedId(id)
      track('clicked allocation guide update', {
        report_month: dateMoment.month(),
        report_year: dateMoment.year(),
      })
    },
    [track]
  )

  const tableBody = useMemo(() => {
    if (Object.keys(allocationGuides).length === 0) {
      return (
        <Table.Row>
          <Table.Cell colSpan={4}>None yet</Table.Cell>
        </Table.Row>
      )
    } else {
      return sortBy(Object.values(allocationGuides), 'date')
        .reverse()
        .map(({ id, date, compiledOn }) => (
          <Table.Row key={id}>
            <Table.Cell>
              {moment(date).format(DATE_FORMATS.MONTH_YEAR)}
            </Table.Cell>
            <Table.Cell>
              {moment(compiledOn).format(DATE_FORMATS.DISPLAY_LONG)}
            </Table.Cell>
            <Table.Cell>
              <Link href={`/allocation-guides/view/${id}`} newPage={false}>
                View Guide
              </Link>
            </Table.Cell>
            <Table.Cell>
              <Dialog>
                <Dialog.Trigger asChild>
                  <Button variant="link" onClick={() => handleUpdate(id, date)}>
                    Update
                  </Button>
                </Dialog.Trigger>
                <Dialog.Content>
                  <Dialog.Title>Confirm Allocation Guide Update</Dialog.Title>
                  <Dialog.Description>
                    Please confirm that you would like to make changes to the
                    selected allocation guide. Changes will be based on the
                    currently set percentages. Would you like to continue?
                  </Dialog.Description>
                  <Dialog.Footer>
                    <Dialog.Close asChild>
                      <Button
                        variant="secondary"
                        onClick={() => setSelectedId(undefined)}
                      >
                        Cancel
                      </Button>
                    </Dialog.Close>

                    <Dialog.Close asChild>
                      <Button onClick={handleUpdateAllocationGuide}>
                        Confirm Changes
                      </Button>
                    </Dialog.Close>
                  </Dialog.Footer>
                </Dialog.Content>
              </Dialog>
            </Table.Cell>
          </Table.Row>
        ))
    }
  }, [allocationGuides, handleUpdate, handleUpdateAllocationGuide])

  return (
    <div className="allocationList">
      <Table>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell className="w-1/3">Month</Table.HeaderCell>
            <Table.HeaderCell className="w-1/3">Date Compiled</Table.HeaderCell>
            <Table.HeaderCell className="w-1/6">Action</Table.HeaderCell>
            <Table.HeaderCell className="w-1/6">Update</Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>{tableBody}</Table.Body>
      </Table>
    </div>
  )
}
